import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { campaignAPI, examSetAPI, sessionAPI, codingQuestionAPI, batchAPI } from '../../services/api';
import { groupBatches } from './batches';
import { USE_DEMO_DATA } from '../../data/demoMode';
import { getDemoPositionById } from '../../data/campaignsDummyData';
import { applyBatchHireBars, groupPeople, totalQuestionCount } from './derive';

const rowsOf = (res, key) => {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  if (key && Array.isArray(d?.[key])) return d[key];
  if (Array.isArray(d?.data)) return d.data;
  return [];
};

/**
 * Everything the position workspace reads, loaded once and shared by all tabs.
 * Uses only existing endpoints:
 *   GET /v1/campaigns/:id, /statistics, /questions, /coding-questions,
 *   /exam-sets, /candidates, GET /v1/sessions/admin/all?campaign_id&summary=true
 */
export default function usePositionData(campaignId, { onUnauthorized } = {}) {
  const [campaign, setCampaign] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [questionsLoading, setQuestionsLoading] = useState(true);
  const [codingQuestions, setCodingQuestions] = useState([]);
  const [codingLoading, setCodingLoading] = useState(true);
  const [examSets, setExamSets] = useState([]);
  const [examSetsLoading, setExamSetsLoading] = useState(true);
  const [examSetsError, setExamSetsError] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(true);
  const [batches, setBatches] = useState([]);
  const [batchesError, setBatchesError] = useState('');
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const reloadCampaign = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      if (USE_DEMO_DATA) {
        const pos = getDemoPositionById(campaignId);
        if (pos && alive.current) { setCampaign({ ...pos }); setStatistics({}); return; }
      }
      const [c, s] = await Promise.all([
        campaignAPI.get(campaignId),
        campaignAPI.getStatistics(campaignId).catch(() => ({ data: {} })),
      ]);
      if (!alive.current) return;
      setCampaign(c.data);
      setStatistics(s.data || {});
    } catch (err) {
      if (err?.response?.status === 401 && onUnauthorized) onUnauthorized();
      if (alive.current && !quiet) setCampaign(null);
    } finally {
      if (alive.current && !quiet) setLoading(false);
    }
  }, [campaignId, onUnauthorized]);

  const reloadQuestions = useCallback(async () => {
    setQuestionsLoading(true);
    try {
      const res = await campaignAPI.listQuestions(campaignId);
      if (alive.current) setQuestions(res.data?.questions || []);
    } catch {
      if (alive.current) setQuestions([]);
    } finally {
      if (alive.current) setQuestionsLoading(false);
    }
  }, [campaignId]);

  const reloadCoding = useCallback(async () => {
    setCodingLoading(true);
    try {
      const res = await codingQuestionAPI.list(campaignId);
      if (alive.current) setCodingQuestions(rowsOf(res));
    } catch {
      if (alive.current) setCodingQuestions([]);
    } finally {
      if (alive.current) setCodingLoading(false);
    }
  }, [campaignId]);

  const reloadExamSets = useCallback(async () => {
    setExamSetsError('');
    try {
      const res = await examSetAPI.list(campaignId);
      if (alive.current) setExamSets(rowsOf(res, 'exam_sets'));
    } catch (err) {
      if (alive.current) setExamSetsError(err.response?.data?.detail || 'Failed to load exams.');
    } finally {
      if (alive.current) setExamSetsLoading(false);
    }
  }, [campaignId]);

  const reloadCandidates = useCallback(async () => {
    try {
      const [c, s] = await Promise.all([
        campaignAPI.listCandidates(campaignId),
        sessionAPI.getAllSessions({ campaign_id: campaignId, summary: true }).catch(() => ({ data: [] })),
      ]);
      if (!alive.current) return;
      setCandidates(rowsOf(c));
      setSessions(rowsOf(s));
    } catch {
      if (alive.current) { setCandidates([]); setSessions([]); }
    } finally {
      if (alive.current) setCandidatesLoading(false);
    }
  }, [campaignId]);

  /** Batches of every interview on this position. */
  const reloadBatches = useCallback(async () => {
    setBatchesError('');
    try {
      const res = await batchAPI.listForCampaign(campaignId);
      if (alive.current) setBatches(rowsOf(res, 'batches'));
    } catch (err) {
      if (alive.current) {
        setBatches([]);
        setBatchesError(err.response?.status === 404 ? 'Batches are not available on this server yet.' : 'Failed to load batches.');
      }
    }
  }, [campaignId]);

  /** Candidate-side changes (assign, invite, reset) also move exam-set counts. */
  const reloadAssignments = useCallback(
    () => Promise.all([reloadCandidates(), reloadExamSets(), reloadBatches()]),
    [reloadCandidates, reloadExamSets, reloadBatches],
  );

  useEffect(() => {
    setCampaign(null);
    setExamSets([]);
    setCandidates([]);
    setSessions([]);
    setExamSetsLoading(true);
    setCandidatesLoading(true);
    reloadCampaign();
    reloadQuestions();
    reloadCoding();
    reloadExamSets();
    reloadCandidates();
    reloadBatches();
  }, [reloadCampaign, reloadQuestions, reloadCoding, reloadExamSets, reloadCandidates, reloadBatches]);

  const batchesByExamSet = useMemo(() => groupBatches(batches), [batches]);
  const batchesById = useMemo(() => Object.fromEntries(batches.map((b) => [b.id, b])), [batches]);

  // Fallback hire bar (0–100) for results outside any batch. Each batch sets its own
  // (batch.min_hire_score), copied onto every result as r.hireBar.
  const hireBar = useMemo(() => {
    const v = Number(campaign?.min_hire_score);
    return Number.isFinite(v) ? Math.round(v) : 50;
  }, [campaign?.min_hire_score]);

  const people = useMemo(
    () => applyBatchHireBars(groupPeople(candidates, sessions), batchesById, hireBar),
    [candidates, sessions, batchesById, hireBar],
  );

  const counts = useMemo(() => ({
    questions: questionsLoading || codingLoading ? null : totalQuestionCount(questions, codingQuestions),
    interviews: examSetsLoading ? null : examSets.length,
    candidates: candidatesLoading ? null : people.length,
  }), [questionsLoading, codingLoading, questions, codingQuestions, examSetsLoading, examSets.length, candidatesLoading, people.length]);

  return {
    campaign, setCampaign, statistics, loading, hireBar,
    questions, setQuestions, questionsLoading, reloadQuestions,
    codingQuestions, codingLoading, reloadCoding,
    examSets, examSetsLoading, examSetsError, reloadExamSets,
    candidates, setCandidates, sessions, candidatesLoading, reloadCandidates, reloadAssignments,
    people, counts, reloadCampaign,
    batches, batchesByExamSet, batchesById, batchesError, reloadBatches,
  };
}
